import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { LOCALES, type ChallengeNotice } from '@chess-uno/shared-protocol'
import { loadSession, type StoredSession } from './api/session.js'
import { getSocket } from './api/socket.js'
import { unlockSound } from './api/sound.js'
import { SeoHead } from './SeoHead.js'
import { LocaleProvider, useI18n } from './i18n/index.js'
import { PAGE_PATHS } from './seo.js'
import { TierBadge } from './components/Competition.js'
import { TournamentToasts } from './components/TournamentToasts.js'
import { AuthGate, HomePage, InviteLanding, JoinGamePage, QueuePage } from './pages/Lobby.js'
import { LegalNoticePage, PrivacyPage } from './pages/Legal.js'
import { ProfilePage } from './pages/Profile.js'
import { RulesPage } from './pages/Rules.js'

const GamePage = lazy(() => import('./pages/Game.js').then((module) => ({ default: module.GamePage })))
const CreateGamePage = lazy(() => import('./pages/Settings.js').then((module) => ({ default: module.CreateGamePage })))
const BotGamePage = lazy(() => import('./pages/Settings.js').then((module) => ({ default: module.BotGamePage })))
const TournamentHubPage = lazy(() => import('./pages/Tournament.js').then((module) => ({ default: module.TournamentHubPage })))
const TournamentPage = lazy(() => import('./pages/Tournament.js').then((module) => ({ default: module.TournamentPage })))
const TournamentInviteLanding = lazy(() => import('./pages/Tournament.js').then((module) => ({ default: module.TournamentInviteLanding })))

/** Challenges received live, on any page. */
function ChallengeToasts({ session }: { session: StoredSession }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [notices, setNotices] = useState<ChallengeNotice[]>([])
  useEffect(() => {
    const socket = getSocket(session)
    const onChallenge = (n: ChallengeNotice) => setNotices((list) => [...list.filter((x) => x.gameId !== n.gameId), n])
    socket.on('challenge:received', onChallenge)
    return () => {
      socket.off('challenge:received', onChallenge)
    }
  }, [session])
  const dismiss = (gameId: string) => setNotices((list) => list.filter((x) => x.gameId !== gameId))
  if (notices.length === 0) return null
  return (
    <div className="toasts" aria-live="polite">
      {notices.map((n) => (
        <div key={n.gameId} className="toast" role="alertdialog" aria-label={t.toast.aria(n.fromName)} data-testid="challenge-toast">
          <p>
            {t.toast.challengesYou(n.fromName)} · <TierBadge rating={n.fromRating} compact /> {n.fromRating} Elo
          </p>
          <div className="toast-actions">
            <button
              type="button"
              data-testid="challenge-accept"
              onClick={() => {
                dismiss(n.gameId)
                navigate(`/join/${n.inviteCode}`)
              }}
            >
              {t.toast.accept}
            </button>
            <button type="button" className="secondary" onClick={() => dismiss(n.gameId)}>
              {t.toast.later}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function Loading() {
  const { t } = useI18n()
  return (
    <main className="settings-page" aria-live="polite">
      {t.common.loading}
    </main>
  )
}

export function App() {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession())
  useEffect(() => {
    window.addEventListener('pointerdown', unlockSound, { once: true })
    window.addEventListener('keydown', unlockSound, { once: true })
    return () => {
      window.removeEventListener('pointerdown', unlockSound)
      window.removeEventListener('keydown', unlockSound)
    }
  }, [])
  // Without a session, a protected page shows the login form in place.
  const guard = (page: (s: StoredSession) => React.ReactNode) => (session ? page(session) : <AuthGate onSession={setSession} />)
  return (
    <BrowserRouter>
      <LocaleProvider>
        <SeoHead />
        {session && <ChallengeToasts session={session} />}
        {session && <TournamentToasts session={session} />}
        <Suspense fallback={<Loading />}>
          <Routes>
            {LOCALES.map((locale) => [
              <Route key={`home-${locale}`} path={PAGE_PATHS.home[locale]} element={<HomePage session={session} onSession={setSession} />} />,
              <Route key={`rules-${locale}`} path={PAGE_PATHS.rules[locale]} element={<RulesPage session={session} />} />,
              <Route key={`legal-${locale}`} path={PAGE_PATHS.legal[locale]} element={<LegalNoticePage session={session} />} />,
              <Route key={`privacy-${locale}`} path={PAGE_PATHS.privacy[locale]} element={<PrivacyPage session={session} />} />,
            ])}
            <Route path="/player/:username" element={<ProfilePage session={session} />} />
            <Route path="/create" element={guard((s) => <CreateGamePage session={s} />)} />
            <Route
              path="/join/:code?"
              element={session ? <JoinGamePage session={session} /> : <InviteLanding onSession={setSession} />}
            />
            <Route path="/queue" element={guard((s) => <QueuePage session={s} />)} />
            <Route path="/bot" element={guard((s) => <BotGamePage session={s} />)} />
            <Route path="/game/:gameId" element={guard((s) => <GamePage session={s} />)} />
            <Route path="/tournament" element={guard((s) => <TournamentHubPage session={s} />)} />
            <Route
              path="/tournament/:code"
              element={session ? <TournamentPage session={session} /> : <TournamentInviteLanding onSession={setSession} />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </LocaleProvider>
    </BrowserRouter>
  )
}
